/**
 * Evidence Preview Component
 *
 * FERP CRITICAL: Evidence attribution display with confidence scoring
 * Shows complete audit trail from source document to extracted template fields
 *
 * FERP Principles:
 * - Complete traceability (every field → source document location)
 * - Confidence scoring for all extractions
 * - Data-driven (evidence from backend ChaosIngestionService)
 * - Life-critical (audit trail for regulatory compliance)
 *
 * Evidence Types:
 * - field_evidence: Maps UI fields to source document excerpts
 * - primitive_evidence: Maps primitives to document sections
 * - compliance_evidence: Maps compliance frameworks to regulatory text
 */

import React, { useState } from 'react';
import {
  Stack,
  Card,
  Group,
  Text,
  Badge,
  Accordion,
  Alert,
  Button,
  RingProgress,
  Center,
  Table,
  Code,
  Tabs,
  Paper,
  Progress,
  ThemeIcon,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconCheck,
  IconAlertCircle,
  IconArrowRight,
  IconFileText,
  IconCode,
  IconShieldCheck,
  IconTrendingUp,
  IconExternalLink,
} from '@tabler/icons-react';
import { useConversionConfidence, type ConversionResult } from '../hooks/useConvertChaos';

export interface EvidencePreviewProps {
  conversionResult: ConversionResult;
  onProceedToRefinement?: () => void;
}

/**
 * Evidence Preview Component
 *
 * FERP Pattern: Display evidence attribution with confidence scores
 */
export function EvidencePreview({
  conversionResult,
  onProceedToRefinement,
}: EvidencePreviewProps): JSX.Element {
  const confidenceAnalysis = useConversionConfidence(conversionResult);
  const [activeTab, setActiveTab] = useState<string>('overview');

  const {
    template,
    evidence,
    confidence,
    requires_human_review,
    review_items,
  } = conversionResult;

  /**
   * Get confidence color based on score
   */
  const getConfidenceColor = (score: number): string => {
    if (score >= 0.9) return 'green';
    if (score >= 0.8) return 'blue';
    if (score >= 0.7) return 'yellow';
    return 'red';
  };

  /**
   * Get confidence label
   */
  const getConfidenceLabel = (score: number): string => {
    if (score >= 0.9) return 'High Confidence';
    if (score >= 0.8) return 'Good Confidence';
    if (score >= 0.7) return 'Moderate Confidence';
    return 'Low Confidence';
  };

  /**
   * Format percentage
   */
  const formatPercent = (value: number): string => {
    return `${Math.round(value * 100)}%`;
  };

  return (
    <Stack gap="md">
      {/* Overall Confidence Summary */}
      <Card withBorder>
        <Group justify="space-between" ta="left">
          <div style={{ flex: 1 }}>
            <Group mb="md">
              <ThemeIcon size="xl" variant="light" color={getConfidenceColor(confidence.overall)}>
                <IconTrendingUp size={24} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="xl">
                  {formatPercent(confidence.overall)} Overall Confidence
                </Text>
                <Text size="sm" c="dimmed">
                  Grade: {confidenceAnalysis.confidenceGrade}
                </Text>
              </div>
            </Group>

            <Group gap="xl">
              <div>
                <Text size="xs" c="dimmed">Template ID</Text>
                <Code>{template.template_id}</Code>
              </div>
              <div>
                <Text size="xs" c="dimmed">Domain</Text>
                <Badge variant="light">{template.business.domain}</Badge>
              </div>
              <div>
                <Text size="xs" c="dimmed">Category</Text>
                <Badge variant="light">{template.business.category}</Badge>
              </div>
            </Group>
          </div>

          <RingProgress
            size={140}
            thickness={12}
            sections={[
              { value: confidence.overall * 100, color: getConfidenceColor(confidence.overall) },
            ]}
            label={
              <Center>
                <Stack ta="center" gap={0}>
                  <Text size="xl" fw={700}>
                    {formatPercent(confidence.overall)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Confidence
                  </Text>
                </Stack>
              </Center>
            }
          />
        </Group>
      </Card>

      {/* Review Required Alert */}
      {requires_human_review && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Human Review Required"
          color="orange"
        >
          <Stack gap="sm">
            <Text size="sm">
              This template requires human review before approval due to:
            </Text>
            <ul style={{ marginTop: 0, marginBottom: 8 }}>
              {confidenceAnalysis.recommendations.map((rec, idx) => (
                <li key={idx}><Text size="sm">{rec}</Text></li>
              ))}
            </ul>
            {onProceedToRefinement && (
              <Button
                leftSection={<IconArrowRight size={16} />}
                variant="light"
                color="orange"
                onClick={onProceedToRefinement}
              >
                Proceed to Template Refinement
              </Button>
            )}
          </Stack>
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'overview')}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconCheck size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="confidence" leftSection={<IconTrendingUp size={16} />}>
            Confidence Breakdown
          </Tabs.Tab>
          <Tabs.Tab value="evidence" leftSection={<IconFileText size={16} />}>
            Field Evidence
          </Tabs.Tab>
          <Tabs.Tab value="primitives" leftSection={<IconCode size={16} />}>
            Primitive Evidence
          </Tabs.Tab>
          <Tabs.Tab value="compliance" leftSection={<IconShieldCheck size={16} />}>
            Compliance Evidence
          </Tabs.Tab>
        </Tabs.List>

        {/* Overview Tab */}
        <Tabs.Panel value="overview" pt="md">
          <Stack gap="md">
            <Card withBorder>
              <Text fw={600} size="lg" mb="md">Business Metadata</Text>
              <Stack gap="xs">
                <Group>
                  <Text w={150} size="sm" c="dimmed">Name:</Text>
                  <Text fw={500}>{template.business.name}</Text>
                </Group>
                <Group>
                  <Text w={150} size="sm" c="dimmed">Description:</Text>
                  <Text>{template.business.description}</Text>
                </Group>
                <Group>
                  <Text w={150} size="sm" c="dimmed">Tags:</Text>
                  <Group gap="xs">
                    {template.business.tags.map((tag, idx) => (
                      <Badge key={idx} size="sm" variant="light">{tag}</Badge>
                    ))}
                  </Group>
                </Group>
              </Stack>
            </Card>

            <Card withBorder>
              <Text fw={600} size="lg" mb="md">UI Structure</Text>
              <Text size="sm" c="dimmed" mb="xs">
                {template.ui.steps.length} steps extracted
              </Text>
              {template.ui.steps.map((step, idx) => (
                <Paper key={idx} withBorder p="sm" mb="xs">
                  <Group justify="space-between">
                    <div>
                      <Text fw={500}>{step.label}</Text>
                      <Text size="xs" c="dimmed">{step.description}</Text>
                    </div>
                    <Badge variant="light">{step.fields.length} fields</Badge>
                  </Group>
                </Paper>
              ))}
            </Card>

            <Card withBorder>
              <Text fw={600} size="lg" mb="md">Primitives Extracted</Text>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm">Workflow Definitions</Text>
                  <Badge>{template.primitives.workflow_definitions?.length || 0}</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Actors</Text>
                  <Badge>{template.primitives.actors?.length || 0}</Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Policies</Text>
                  <Badge>{template.primitives.policies?.length || 0}</Badge>
                </Group>
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>

        {/* Confidence Breakdown Tab */}
        <Tabs.Panel value="confidence" pt="md">
          <Stack gap="md">
            {[
              { label: 'Business Metadata', value: confidence.business_metadata, key: 'business' },
              { label: 'UI Structure', value: confidence.ui_structure, key: 'ui' },
              { label: 'Primitive Mapping', value: confidence.primitive_mapping, key: 'primitives' },
              { label: 'Field Mapping', value: confidence.field_mapping, key: 'mappings' },
              { label: 'Compliance Detection', value: confidence.compliance_detection, key: 'compliance' },
            ].map((item) => (
              <Card key={item.key} withBorder>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text fw={500}>{item.label}</Text>
                    <Badge
                      size="lg"
                      variant="light"
                      color={getConfidenceColor(item.value)}
                    >
                      {formatPercent(item.value)}
                    </Badge>
                  </Group>
                  <Progress
                    value={item.value * 100}
                    size="lg"
                    color={getConfidenceColor(item.value)}
                  />
                  <Text size="xs" c="dimmed">
                    {getConfidenceLabel(item.value)}
                  </Text>
                </Stack>
              </Card>
            ))}
          </Stack>
        </Tabs.Panel>

        {/* Field Evidence Tab */}
        <Tabs.Panel value="evidence" pt="md">
          <Card withBorder>
            <Text fw={600} size="lg" mb="md">Field Evidence Attribution</Text>
            <Text size="sm" c="dimmed" mb="md">
              Every extracted field is traced back to its source location in the document
            </Text>

            {review_items.length > 0 ? (
              <Accordion>
                {review_items.map((item, idx) => (
                  <Accordion.Item key={idx} value={`item-${idx}`}>
                    <Accordion.Control>
                      <Group>
                        <Text fw={500}>{item.field_path}</Text>
                        <Badge
                          size="sm"
                          variant="light"
                          color={getConfidenceColor(item.confidence)}
                        >
                          {formatPercent(item.confidence)}
                        </Badge>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="xs">
                        <div>
                          <Text size="xs" c="dimmed">Issue:</Text>
                          <Text size="sm">{item.issue}</Text>
                        </div>
                        <div>
                          <Text size="xs" c="dimmed">Suggestion:</Text>
                          <Text size="sm">{item.suggestion}</Text>
                        </div>
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            ) : (
              <Alert icon={<IconCheck size={16} />} color="green">
                All fields extracted with high confidence. No review items.
              </Alert>
            )}
          </Card>
        </Tabs.Panel>

        {/* Primitive Evidence Tab */}
        <Tabs.Panel value="primitives" pt="md">
          <Card withBorder>
            <Text fw={600} size="lg" mb="md">Primitive Evidence</Text>
            <Text size="sm" c="dimmed">
              Evidence attribution for 16 primitive type mappings
            </Text>
            <Code block mt="md">
              {JSON.stringify(evidence.primitive_evidence || {}, null, 2)}
            </Code>
          </Card>
        </Tabs.Panel>

        {/* Compliance Evidence Tab */}
        <Tabs.Panel value="compliance" pt="md">
          <Card withBorder>
            <Text fw={600} size="lg" mb="md">Compliance Framework Evidence</Text>
            <Stack gap="md">
              <Group>
                <Text size="sm" c="dimmed">Detected Frameworks:</Text>
                <Group gap="xs">
                  {template.compliance.frameworks.map((framework, idx) => (
                    <Badge key={idx} size="lg" variant="light" color="blue">
                      {framework}
                    </Badge>
                  ))}
                </Group>
              </Group>

              {template.compliance.frameworks.length === 0 && (
                <Alert icon={<IconAlertCircle size={16} />} color="yellow">
                  No compliance frameworks detected. Consider adding manually if required.
                </Alert>
              )}

              <Code block>
                {JSON.stringify(evidence.compliance_evidence || {}, null, 2)}
              </Code>
            </Stack>
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
